/**
 * "Request access" for any screen that refuses a user. Inline styles on purpose:
 * the error boundary renders this when the app's stylesheet may be the thing
 * that broke, and the in-app refusals share the same look. The sent state is
 * remembered per user and resource for the session, so a refresh shows
 * "Request sent" instead of inviting a duplicate.
 */
import { useState } from 'react';
import { Check, Loader2, Send } from '@/shared/ui/icons';
import type { AccessRequestTarget } from '../utils/accessRequestTarget';
import {
  ACCESS_REQUEST_MAX_NOTE,
  postAccessRequest,
  readAccessRequestSent,
  rememberAccessRequestSent,
} from '../services/AccessRequestService';

type Phase = 'idle' | 'sending' | 'sent';

export function RequestAccess({ target, audience = 'project' }: {
  target: AccessRequestTarget;
  /** Whose admins hear about it, for the confirmation line. */
  audience?: 'project' | 'workspace';
}) {
  const remembered = readAccessRequestSent(target);
  const [phase, setPhase] = useState<Phase>(remembered ? 'sent' : 'idle');
  const [viaSupport, setViaSupport] = useState(remembered === 'support');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setPhase('sending');
    setError(null);
    const outcome = await postAccessRequest(target, note);
    if (!outcome.ok) {
      setPhase('idle');
      setError(outcome.error);
      return;
    }
    rememberAccessRequestSent(target, outcome.fallback);
    setViaSupport(outcome.fallback === 'support');
    setPhase('sent');
  };

  const sent = phase === 'sent';
  const confirmation = viaSupport
    ? "We've passed this to our support team."
    : `We've let the ${audience}'s admins know.`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', width: '100%', maxWidth: '320px' }}>
      {!sent && (
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={ACCESS_REQUEST_MAX_NOTE}
          placeholder="Add a note (optional)"
          aria-label="Add a note (optional)"
          disabled={phase === 'sending'}
          onKeyDown={(e) => { if (e.key === 'Enter') void send(); }}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '10px 12px',
            borderRadius: '10px',
            border: '1px solid var(--border, #e5e7eb)',
            background: 'var(--background, #fff)',
            color: 'var(--foreground, #111)',
            fontFamily: 'inherit',
            fontSize: '13.5px',
            outline: 'none',
          }}
        />
      )}
      <button
        type="button"
        onClick={() => void send()}
        disabled={phase !== 'idle'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '9px',
          padding: '12px 26px',
          borderRadius: '11px',
          background: sent ? 'transparent' : 'var(--brand, #5d147c)',
          color: sent ? 'var(--muted-foreground, #6b7280)' : '#fff',
          border: sent ? '1px solid var(--border, #e5e7eb)' : 'none',
          fontFamily: 'inherit',
          fontSize: '14.5px',
          fontWeight: 600,
          letterSpacing: '-0.1px',
          cursor: phase === 'idle' ? 'pointer' : 'default',
          opacity: phase === 'sending' ? 0.7 : 1,
        }}
      >
        {phase === 'sending' ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
          : sent ? <Check size={15} strokeWidth={2.25} /> : <Send size={15} strokeWidth={2.25} />}
        {sent ? 'Request sent' : 'Request access'}
      </button>
      {sent && (
        <span role="status" style={{ fontSize: '12.5px', color: 'var(--muted-foreground, #6b7280)' }}>{confirmation}</span>
      )}
      {error && (
        <span role="alert" style={{ fontSize: '12.5px', color: 'var(--destructive, #dc2626)' }}>{error}</span>
      )}
    </div>
  );
}
